import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { CleanupLogFilters, CleanupLogResponse } from './types';

/**
 * Builds the list endpoint's query string from the filter state, leaving out
 * empty filters so the server applies none.
 * @param {CleanupLogFilters} filters - The page's filter and pagination state
 * @return {URLSearchParams} The search params for the list request
 */
function cleanupLogParams(filters: CleanupLogFilters): URLSearchParams {
  const params = new URLSearchParams();
  filters.entityTypes.forEach((t) => params.append('entity_types[]', t));
  if (filters.provenance !== '')
    params.append('provenance', filters.provenance);
  if (filters.after !== '') params.append('after', filters.after);
  if (filters.before !== '') params.append('before', filters.before);
  if (filters.search !== '') params.append('search', filters.search);
  params.append('page', filters.page.toString());
  params.append('per_page', filters.perPage.toString());
  return params;
}

/**
 * The clean-up log page for a filter state: its key and fetcher together.
 * @param {CleanupLogFilters} filters - The page's filter and pagination state
 * @return The queryOptions for that page of the log
 */
export const cleanupLogQueryOptions = (filters: CleanupLogFilters) =>
  queryOptions({
    queryKey: ['entity-cleanup-log', filters],
    queryFn: ({ signal }) =>
      api
        .get('admin/entity_cleanup_logs', {
          searchParams: cleanupLogParams(filters),
          signal,
        })
        .json<CleanupLogResponse>(),
    // Holds the current page on screen while the next one loads.
    placeholderData: keepPreviousData,
  });

/**
 * One page of the clean-up log, re-fetched whenever the filters change.
 * @param {CleanupLogFilters} filters - The page's filter and pagination state
 * @return The query result for that page of the log
 */
export const useCleanupLog = (filters: CleanupLogFilters) =>
  useQuery(cleanupLogQueryOptions(filters));
